import React, { Component } from "react";
import { Box, Grid } from "@mui/material";
import { withStyles } from "@mui/styles";
import { withRouter } from "react-router-dom";
import queryString from "query-string";
import getTrans from "../../../languages/getTrans";
import { ServiceCard, SearchInput } from "./components";

const ROW_HEIGHT = 50;
const ITEMS_PER_ROW = 1;
const BUFFER_ROWS = 10;
const INITIAL_ITEMS = 50;
const SCROLL_THROTTLE = 16;
const SEARCH_DEBOUNCE = 300;

class Services extends Component {
	state = { visibleRange: { start: 0, end: INITIAL_ITEMS }, search: "", searchFilter: "" };
	scrollContainerRef = React.createRef();
	scrollTimeout = null;
	searchTimeout = null;
	filteredServices = [];

	componentDidMount = async () => {
		const searchFromUrl = queryString.parse(this.props.location.search).q || "";
		if (searchFromUrl) {
			this.setState({ search: searchFromUrl, searchFilter: searchFromUrl });
		}
		await this.getServices();
		this.initServices();
		this.setupScrollListener();
	}
	componentDidUpdate(prevProps, prevState) {
		const prevQuery = queryString.parse(prevProps.location?.search || "").q;
		const currentQuery = queryString.parse(this.props.location.search).q;
		if (prevQuery !== currentQuery) {
			const searchFromUrl = currentQuery || "";
			this.setState({ search: searchFromUrl, searchFilter: searchFromUrl });
		}
		if (prevProps.services !== this.props.services && this.props.services?.length) {
			this.initServices();
		}
		if (prevState.searchFilter !== this.state.searchFilter) {
			this.filterServices();
			this.scrollContainerRef.current?.scrollTo(0, 0);
			this.updateVisibleRange();
		}
		if (!this.filteredServices.length && this.props.services?.length && !this.state.searchFilter) {
			this.initServices();
		}
	}
	componentWillUnmount = () => {
		this.removeScrollListener();
		if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
	}

	getServices = async () => {
		if (this.props.services?.length) return;
		await this.props.getServices();
	}
	initServices = () => {
		this.filterServices();
		this.updateVisibleRange();
	}
	filterServices = () => {
		const { services = [] } = this.props;
		const { searchFilter } = this.state;
		if (!searchFilter.trim()) {
			this.filteredServices = services;
			return;
		}
		const searchLower = searchFilter.toLowerCase().trim();
		this.filteredServices = services.filter(service => {
			const name = String(service?.name || "").toLowerCase();
			const nameEnRu = String(service?.nameTranscriptionEnRu || "").toLowerCase();
			const nameRuEn = String(service?.nameTranscriptionRuEn || "").toLowerCase();
			return name.includes(searchLower) || nameEnRu.includes(searchLower) || nameRuEn.includes(searchLower);
		});
	}

	updateVisibleRange = () => {
		this.setState({ visibleRange: { start: 0, end: Math.min(INITIAL_ITEMS, this.filteredServices.length) } });
	}
	setupScrollListener = () => {
		this.scrollContainerRef.current?.addEventListener('scroll', this.onScroll, { passive: true });
	}
	removeScrollListener = () => {
		this.scrollContainerRef.current?.removeEventListener('scroll', this.onScroll);
	}
	onScroll = () => {
		if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
		this.scrollTimeout = setTimeout(() => {
			const container = this.scrollContainerRef.current;
			if (!container) return;
			const scrollTop = container.scrollTop;
			const containerHeight = container.clientHeight;
			const startRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS);
			const endRow = Math.min(
				Math.ceil(this.filteredServices.length / ITEMS_PER_ROW),
				Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_ROWS
			);
			this.setState({
				visibleRange: {
					start: startRow * ITEMS_PER_ROW,
					end: Math.min(endRow * ITEMS_PER_ROW, this.filteredServices.length)
				}
			});
		}, SCROLL_THROTTLE);
	}

	onSearchChange = ({ target }) => {
		const value = target?.value || "";
		this.setState({ search: value });
		this.updateUrlSearch(value);
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(() => {
			this.setState({ searchFilter: value });
		}, SEARCH_DEBOUNCE);
	}
	onSearchClear = () => {
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		this.setState({ search: "", searchFilter: "" });
		this.updateUrlSearch("");
	}
	updateUrlSearch = (value) => {
		const { history, location } = this.props;
		const query = queryString.parse(location.search);
		if (value.trim()) {
			query.q = value;
		} else {
			delete query.q;
		}
		const searchString = queryString.stringify(query);
		history.replace(searchString ? `${location.pathname}?${searchString}` : location.pathname);
	}

	render() {
		const { isLoading, classes, services } = this.props;
		const { visibleRange, search } = this.state;
		const hasServices = services?.length > 0;
		const hasFilteredServices = this.filteredServices.length > 0;

		if (isLoading || !hasServices) {
			return (
				<Box className={classes.root}>
					<SearchInput value={search} onChange={this.onSearchChange} onClear={this.onSearchClear} />
					<Box className={classes.loadingContainer}>
						{isLoading ? getTrans('services.Загрузка...') : getTrans('services.Нет сервисов')}
					</Box>
				</Box>
			);
		}
		if (!hasFilteredServices) {
			return (
				<Box className={classes.root}>
					<SearchInput value={search} onChange={this.onSearchChange} onClear={this.onSearchClear} />
					<Box className={classes.loadingContainer}>
						{getTrans('common.По вашему запросу ничего не найдено')}
					</Box>
				</Box>
			);
		}

		const visibleItems = this.filteredServices.slice(visibleRange.start, visibleRange.end);
		const startRow = Math.floor(visibleRange.start / ITEMS_PER_ROW);
		const offsetTop = startRow * ROW_HEIGHT;
		const totalHeight = Math.ceil(this.filteredServices.length / ITEMS_PER_ROW) * ROW_HEIGHT;
		const bottomSpacerHeight = totalHeight - offsetTop - (visibleItems.length * ROW_HEIGHT);

		return (
			<Box className={classes.root}>
				<SearchInput value={search} onChange={this.onSearchChange} onClear={this.onSearchClear} />
				<Box ref={this.scrollContainerRef} className={classes.gridContainer}>
					{visibleRange.start > 0 && <div style={{ height: offsetTop }} />}
					<Grid container spacing={1}>
						{visibleItems.map((service, index) => (
							<Grid item xs={12} key={service.id || visibleRange.start + index}>
								<ServiceCard item={service} />
							</Grid>
						))}
					</Grid>
					{visibleRange.end < this.filteredServices.length && <div style={{ height: bottomSpacerHeight }} />}
				</Box>
			</Box>
		);
	}
}

const styles = (theme) => ({
	root: {
		minHeight: 0,
		width: "100%",
		height: "100%",
		display: "flex",
		flexDirection: "column",
	},
	gridContainer: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		overflowX: "hidden",
		WebkitOverflowScrolling: "touch",
		padding: "8px",
		borderRadius: 12,
		margin: "0 12px",
		boxSizing: "border-box",
		backgroundColor: theme.palette.header.default,
	},
	loadingContainer: {
		padding: 32,
		textAlign: "center",
		color: theme.palette.text.main,
	},
});

export default withRouter(withStyles(styles)(Services))
